import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { localizationService } from './services/localizationService';

const AccreditationBadge = ({ level, variant = 'medallion', publishedAt, langKey = 'en' }) => {
  const [labels, setLabels] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [medallionTextScale, setMedallionTextScale] = useState(1);
  const medallionTextViewportRef = useRef(null);
  const medallionTextInnerRef = useRef(null);

  // 1. Calculate Expiry & Load Translations
  useEffect(() => {
    // Expiry Logic: 24 Months
    if (publishedAt?.seconds) {
      const pubDate = new Date(publishedAt.seconds * 1000);
      const expiryDate = new Date(pubDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 2);
      setIsExpired(Date.now() > expiryDate.getTime());
    }

    const sourceLabels = {
      foundation: { tier: "Foundation", title: "Inclusive School" },
      certified: { tier: "Certified", title: "Inclusive School" },
      global: { tier: "Global Accreditation", title: "IFIP Inclusive School" },
      pending: "Accreditation Pending / Contact IFIP"
    };

    const fetchLabels = async () => {
      // Use the V2 Surgical Engine for badges
      const translated = await localizationService.sync(langKey, 'badges', sourceLabels, 'v2_master_en', 'v2_translations');
      setLabels(translated);
    };

    fetchLabels();
  }, [langKey, publishedAt]);

  // Map the level prop to the CSS color classes used in your original design
  const tierMap = { foundation: 'bronze', certified: 'silver', global: 'gold' };
  const safeLabels = labels || {
    foundation: { tier: "Foundation", title: "Inclusive School" },
    certified: { tier: "Certified", title: "Inclusive School" },
    global: { tier: "Global Accreditation", title: "IFIP Inclusive School" },
    pending: "Accreditation Pending / Contact IFIP"
  };
  const currentLevel = safeLabels[level] || safeLabels.foundation;
  const statusClass = isExpired ? 'expired' : (tierMap[level] || 'bronze');

  useLayoutEffect(() => {
    if (!labels) return;
    if (variant !== 'medallion' || isExpired) {
      setMedallionTextScale(1);
      return;
    }

    const viewport = medallionTextViewportRef.current;
    const inner = medallionTextInnerRef.current;
    if (!viewport || !inner) return;

    // Reset before measuring natural text size.
    inner.style.transform = 'scale(1)';
    const vw = viewport.clientWidth || 1;
    const vh = viewport.clientHeight || 1;
    const iw = inner.scrollWidth || 1;
    const ih = inner.scrollHeight || 1;
    const widthScale = vw / iw;
    const heightScale = vh / ih;
    const nextScale = Math.min(1, widthScale, heightScale);
    setMedallionTextScale(Number.isFinite(nextScale) ? nextScale : 1);
  }, [labels, variant, isExpired, currentLevel?.tier, currentLevel?.title, langKey]);

  if (!labels) return null;

  return (
    <div className={`ifip-badge-wrapper ${isExpired ? 'expired' : ''}`}>
      <style>{`
        :root {
          --ifip-blue: #0a254d;
          --gold-grad: linear-gradient(135deg, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c);
          --silver-grad: linear-gradient(135deg, #757575, #e0e0e0, #8e8e8e, #f5f5f5, #616161);
          --bronze-grad: linear-gradient(135deg, #804a00, #ecb176, #a05a2c, #ffd0a8, #633517);
          --shadow-premium: 0 10px 30px rgba(10, 37, 77, 0.12);
        }

        .ifip-badge-wrapper {
          font-family: 'Times New Roman', serif;
          display: inline-flex;
          transition: transform 0.3s ease;
        }

        .ifip-badge-wrapper.expired {
          filter: grayscale(1);
          opacity: 0.7;
        }

        /* MEDALLION STYLE */
        .medallion {
          width: 220px; height: 220px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          box-shadow: var(--shadow-premium);
          text-align: center;
        }

        .medallion::before {
          content: ''; position: absolute; inset: 8px;
          border-radius: 50%; border: 2px solid transparent;
          background-origin: border-box; background-clip: content-box, border-box;
          z-index: 0;
        }

        .medallion.bronze::before { background-image: linear-gradient(white, white), var(--bronze-grad); }
        .medallion.silver::before { background-image: linear-gradient(white, white), var(--silver-grad); }
        .medallion.gold::before   { background-image: linear-gradient(white, white), var(--gold-grad); }
        .medallion.expired::before { border: 2px dashed #999; background: none; }

        /* BANNER STYLE */
        .banner {
          display: flex; align-items: center;
          background: white; border-radius: 4px;
          box-shadow: var(--shadow-premium);
          overflow: hidden;
          border: 1px solid #e0e0e0; min-width: 400px;
        }
        .banner-edge { width: 10px; align-self: stretch; }
        .banner.bronze .banner-edge { background: var(--bronze-grad); }
        .banner.silver .banner-edge { background: var(--silver-grad); }
        .banner.gold .banner-edge   { background: var(--gold-grad); }
        .banner.expired .banner-edge { background: #666; }

        /* --- STYLE 3: MINIMALIST SEAL --- */
        .seal {
            height: 60px;          /* Forces the bar to stay this height */
            white-space: nowrap;   /* Stops text from wrapping to a second line */
            align-items: center;
            display: inline-flex; align-items: center; gap: 20px;
            padding: 12px 35px; background: white;
            border-radius: 50px; box-shadow: 0 4px 10px rgba(0,0,0,0.05);
            border: 1px solid #eee; position: relative;
            overflow: hidden; /* This clips the line perfectly to the bottom curve */
        }

        .seal::after {
            content: ''; position: absolute; 
            bottom: 0; left: 0; right: 0;
            height: 6px; 
            z-index: 1;
        }

        .seal.bronze::after { background: var(--bronze-grad); }
        .seal.silver::after { background: var(--silver-grad); }
        .seal.gold::after   { background: var(--gold-grad); }

        .logo-img { object-fit: contain; z-index: 1; position: relative; }
        .medallion .logo-img {
          width: 112px;
          height: 112px;
          margin-top: -10px;
          margin-left: auto;
          margin-right: auto;
          display: block;
        }
        .medallion-text-viewport {
          width: 168px;
          min-height: 70px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          overflow: hidden;
          margin: 0 auto;
        }
        .medallion-text-inner {
          text-align: center;
          transform-origin: top center;
          width: max-content;
          max-width: 100%;
        }
        .banner .logo-img { width: 90px; height: 90px; margin: 0; }
        .seal .logo-img { 
            width: 70px; 
            height: 70px; 
            margin-top: -5px;      /* Pulls the logo up so it doesn't push the bottom down */
            flex-shrink: 0;        /* Prevents the logo from getting squashed by long text */
        }

        .tier-text {
          font-family: 'Segoe UI', sans-serif; font-size: 1.3rem;
          text-transform: none; letter-spacing: 0; font-weight: 700;
          z-index: 1; position: relative;
        }
        .bronze .tier-text { color: #804a00; }
        .silver .tier-text { color: #666; }
        .gold .tier-text   { color: #aa771c; }
        
        .title-text { 
          font-size: 1.2rem; font-weight: bold; color: var(--ifip-blue); line-height: 1.2; 
          z-index: 1; position: relative;
        }
        .expired-notice { 
          font-size: 0.7rem; font-weight: bold; color: #d11d7e; text-transform: uppercase; 
          z-index: 1; position: relative;
        }
      `}</style>

      {/* VARIANT: MEDALLION */}
      {variant === 'medallion' && (
        <div className={`medallion ${statusClass}`}>
          <img src="/images/01%20IFIP%20Logo.png" alt="IFIP" className="logo-img" />
          <div style={{ zIndex: 1, padding: '0 10px', position: 'relative' }}>
            {isExpired ? (
              <div className="expired-notice">{labels.pending}</div>
            ) : (
              <div className="medallion-text-viewport" ref={medallionTextViewportRef}>
                <div
                  className="medallion-text-inner"
                  ref={medallionTextInnerRef}
                  style={{ transform: `scale(${medallionTextScale})` }}
                >
                  <div className="tier-text">{currentLevel.tier}</div>
                  <div className="title-text">{currentLevel.title}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VARIANT: BANNER */}
      {variant === 'banner' && (
        <div className={`banner ${statusClass}`}>
          <div className="banner-edge"></div>
          <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '18px' }}>
            <img src="/images/01%20IFIP%20Logo.png" alt="IFIP" className="logo-img" />
            <div>
              {isExpired ? (
                <div className="expired-notice">{labels.pending}</div>
              ) : (
                <>
                  <h3 className="tier-text" style={{ margin: 0, fontSize: '2.2rem', lineHeight: 1.02 }}>{currentLevel.tier}</h3>
                  <p style={{ margin: '0 0 0', fontSize: '1.65rem', textTransform: 'uppercase', color: '#777', fontFamily: 'Segoe UI', lineHeight: 1.0 }}>{currentLevel.title}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VARIANT: SEAL */}
      {variant === 'seal' && (
        <div className={`seal ${statusClass}`}>
          <img src="/images/01%20IFIP%20Logo.png" alt="IFIP" className="logo-img" />
          <div className="tier-text" style={{ fontFamily: 'Segoe UI', fontWeight: 700, fontSize: '2.05rem', letterSpacing: 0, textTransform: 'none', lineHeight: 1.3 }}>
            {isExpired ? labels.pending : `${currentLevel.tier} ${currentLevel.title}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccreditationBadge;